/* Common Imports*/

import { styled } from "@mui/system";
import { NextRouter } from "next/router";
import React, { useRef } from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	UpdateAmenitiesThunk,
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	Autocomplete,
	Checkbox,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	Snackbar,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { Close, EditOutlined } from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

/* Styling */

const PaperContainer = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const ContainerOne = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 0rem 0.5rem 0rem",
	flexWrap: "wrap",
	gap: "1rem",
}));

// const ContainerMobile = styled("div")(({ theme }) => ({
// 	flexWrap: "wrap",
// 	gap: "1rem",
// 	[theme.breakpoints.down("sm")]: {
// 		width: "100%",
// 		maxHeight: "13rem",
// 		padding: "0.5rem 0rem 0.5rem 0rem",
// 		gap: "1rem",
// 		display: "flex",
// 		flexDirection: "column",
// 		justifyContent: "space-between",
// 		alignItems: "center",
// 		overflow: "scroll",
// 		"&::-webkit-scrollbar": {
// 			display: "none",
// 		},
// 	},
// 	[theme.breakpoints.up("sm")]: {
// 		display: "none",
// 	},
// }));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	height: "4rem",
	width: "100%",
	overflow: "wrap",
	padding: "0.75rem 0.75rem 0.75rem 0.75rem",
	background: theme.palette.background.default,
	borderRadius: "16px",
	gap: "0.5rem",
	/* XS breakpoint */
	[theme.breakpoints.up("xs")]: {
		width: "46.5%",
	},
	/*iphone se - 375*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
		width: "47.5%",
	},
	/*iphone XR - 410*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		width: "47.5%",
	},
	[theme.breakpoints.up("xsPlus")]: {
		width: "48.2%",
	},
	[theme.breakpoints.up("sm")]: {
		width: "48.2%",
	},
	/*ipad Mini */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		width: "48.7%",
	},
	/* ipad Air*/
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		width: "23.2%",
	},
	[theme.breakpoints.up("md")]: {
		width: "48.57%",
	},
	/* MDLG Breakpoint iPadPro*/
	[theme.breakpoints.up(theme.breakpoints.values.md + 64)]: {
		width: "48.65%",
	},
	/*720p and 768p breakpoint */
	[theme.breakpoints.up("lg")]: {
		width: "23.5%",
	},
	/* 1080p 125% breakpoint*/
	[theme.breakpoints.up(theme.breakpoints.values.lg + 150)]: {
		width: "23.76%",
	},
	/* 1080p breakpoint*/
	[theme.breakpoints.up("xl")]: {
		width: "23.54%",
	},
	/* XXL breakpoint  2560p*/
	[theme.breakpoints.up(theme.breakpoints.values.xl + 640)]: {
		width: "23.79%",
	},
	/*4k breakpoint 3840p*/
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		width: "23.89%",
	},
}));

const ImgTag = styled("img")(({ theme }) => ({
	height: "1.75rem",
	width: "1.75rem",
	[theme.breakpoints.up("md1190")]: {
		height: "2rem",
		width: "2rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	fontWeight: "500",
	textAlign: "left",
	fontSize: "0.75rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const ShowMore = styled(Typography)(({ theme }) => ({
	margin: "0.25rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "500",
	color: theme.palette.primary.main,
	textAlign: "center",
	fontSize: "0.85rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		margin: "0.5rem 0rem 0rem 0rem",
	},
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	// marginTop: "2.8rem",
	// [theme.breakpoints.down("sm")]: {
	// 	marginTop: "3.8rem",
	// },
}));

/* Amenity Interface */

interface amenity {
	id: string;
	label: string;
	icon: string;
}

/* Amenities List */

const AmenityOptions = [
	{
		id: "jogging-track",
		label: "Jogging Track",
		icon: "amenities/joggingtrack",
	},
	{
		id: "swimming-pool",
		label: "Swimming Pool",
		icon: "amenities/swimming",
	},
	{
		id: "heated-swimming-pool",
		label: "Heated Swimming Pool",
		icon: "amenities/swimming",
	},
	{
		id: "infinity-pool",
		label: "Infinity Pool",
		icon: "amenities/swimming",
	},
	{
		id: "toddler-pool",
		label: "Toddler Pool",
		icon: "amenities/kidspool",
	},
	{
		id: "clubhouse",
		label: "Clubhouse",
		icon: "amenities/club",
	},
	{
		id: "bonfire-area",
		label: "Bonfire Area",
		icon: "amenities/bonfire",
	},
	{
		id: "park",
		label: "Park",
		icon: "amenities/park",
	},
	{
		id: "senior-citizen-park",
		label: "Senior Citizens Park",
		icon: "amenities/park",
	},
	{
		id: "rain-water-harvesting",
		label: "Rainwater Harvesting",
		icon: "amenities/rainwater",
	},
	{
		id: "security",
		label: "Security",
		icon: "amenities/security",
	},
	{
		id: "gym",
		label: "Gym",
		icon: "amenities/gym",
	},
	{
		id: "cctv-surveillance",
		label: "CCTV Surveillance",
		icon: "amenities/cctv",
	},
	{
		id: "amphitheatre",
		label: "Amphitheatre",
		icon: "amenities/amphitheatre",
	},
	{
		id: "mini-theatre",
		label: "Mini Theatre",
		icon: "amenities/minitheatre",
	},
	{
		id: "yoga-deck",
		label: "Yoga Deck",
		icon: "amenities/yoga",
	},
	{
		id: "landscaped-garden",
		label: "Landscaped Garden",
		icon: "amenities/landscape",
	},
	{
		id: "kids-play-area",
		label: "Kids Play Area",
		icon: "amenities/kidsplayarea",
	},
	{
		id: "supermarket",
		label: "Supermarket",
		icon: "amenities/supermarket",
	},
	{
		id: "atm",
		label: "ATM",
		icon: "amenities/atm",
	},
	{
		id: "pharmacy",
		label: "Pharmacy",
		icon: "amenities/pharmacy",
	},
	{
		id: "tennis-court",
		label: "Tennis Court",
		icon: "amenities/tennis",
	},
	{
		id: "basketball-court",
		label: "Basketball Court",
		icon: "amenities/basketball",
	},
	{
		id: "badminton-court",
		label: "Badminton Court",
		icon: "amenities/badminton",
	},
	{
		id: "table-tennis",
		label: "Table Tennis",
		icon: "amenities/tabletennis",
	},
	{
		id: "indoor-games",
		label: "Indoor Games",
		icon: "amenities/indoor_games",
	},
	{
		id: "billiards",
		label: "Billiards",
		icon: "amenities/billiards",
	},
	{
		id: "skating rink",
		label: "Skating Rink",
		icon: "amenities/skating",
	},
	{
		id: "volleyball-court",
		label: "Volleyball Court",
		icon: "amenities/volleyball",
	},
	{
		id: "sand-pit",
		label: "Sand Pit",
		icon: "amenities/sand-pit",
	},
	{
		id: "cricket-practice-net",
		label: "Cricket Practice Net",
		icon: "amenities/cricket",
	},
	{
		id: "football-turf",
		label: "Football Turf",
		icon: "amenities/football",
	},
	{
		id: "lift",
		label: "Lift",
		icon: "amenities/lift",
	},
	{
		id: "cafeteria",
		label: "Cafeteria",
		icon: "amenities/canteen",
	},
	{
		id: "car-parking",
		label: "Car Parking",
		icon: "amenities/parking",
	},
	{
		id: "covered-car-parking",
		label: "Covered Car Parking",
		icon: "amenities/coveredparking",
	},
	{
		id: "ev-charging-station",
		label: "EV Charging Station",
		icon: "amenities/chargingstation",
	},
	{
		id: "library",
		label: "Library",
		icon: "amenities/library",
	},
	{
		id: "reading-room",
		label: "Reading Room",
		icon: "amenities/readingroom",
	},
	{
		id: "multipurpose-hall",
		label: "Multipurpose Hall",
		icon: "amenities/club",
	},
	{
		id: "jacuzzi",
		label: "Jacuzzi",
		icon: "amenities/jacuzzi",
	},
	{
		id: "sauna",
		label: "Sauna",
		icon: "amenities/sauna",
	},
	{
		id: "spa",
		label: "Spa",
		icon: "amenities/spa",
	},
	{
		id: "barbecue-area",
		label: "Barbecue Area",
		icon: "amenities/barbecue",
	},
	{
		id: "squash-court",
		label: "Squash Court",
		icon: "amenities/squash",
	},
	{
		id: "power-backup",
		label: "Power Backup",
		icon: "amenities/powerbackup",
	},
	{
		id: "helipad",
		label: "Helipad",
		icon: "amenities/helipad",
	},
	{
		id: "sewage-treatment-plant",
		label: "Sewage Treatment Plant",
		icon: "amenities/stp",
	},
	{
		id: "intercom",
		label: "Intercom",
		icon: "amenities/intercom",
	},
	{
		id: "water-softening-plant",
		label: "Water Softening Plant",
		icon: "amenities/watersofteningtank",
	},
	{
		id: "fire-safety-system",
		label: "Fire Safety System",
		icon: "amenities/firesafety",
	},
	{
		id: "butterfly-garden",
		label: "Butterfly Garden",
		icon: "amenities/butterflygarden",
	},
	{
		id: "cycling-track",
		label: "Cycling Track",
		icon: "amenities/cycling",
	},
	{
		id: "waiting-lounge",
		label: "Waiting Lounge",
		icon: "amenities/waitinglounge",
	},
	{
		id: "solar-lighting",
		label: "Solar Lighting",
		icon: "amenities/solarlighting",
	},
	{
		id: "salon",
		label: "Salon",
		icon: "amenities/salon",
	},
	{
		id: "meditation-zone",
		label: "Meditation Zone",
		icon: "amenities/meditation",
	},
	{
		id: "maintenance-staff",
		label: "Maintenance Staff",
		icon: "amenities/maintenance_staff",
	},
	{
		id: "visitor-car-park",
		label: "Visitor Car Parking",
		icon: "amenities/visitor_car_parking",
	},
	{
		id: "outdoor-lounge",
		label: "Outdoor Lounge",
		icon: "amenities/outdoorlounge",
	},
	{
		id: "health-clinic",
		label: "Health Clinic",
		icon: "amenities/hospital",
	},
	{
		id: "restaurant",
		label: "Restaurant",
		icon: "amenities/restaurant",
	},
	{
		id: "golf",
		label: "Golf",
		icon: "amenities/golf",
	},
	{
		id: "waste-management-plant",
		label: "Waste Management Plant",
		icon: "amenities/wastemanagement",
	},
	{
		id: "business-centre",
		label: "Business Centre",
		icon: "amenities/businesscentre",
	},
	{
		id: "party-counter",
		label: "Party Counter",
		icon: "amenities/party",
	},
	{
		id: "service-lift",
		label: "Service Lift",
		icon: "amenities/servicelift",
	},
	{
		id: "gas-pipeline",
		label: "Gas Pipeline",
		icon: "amenities/gaspipeline",
	},
	{
		id: "playschool",
		label: "Play School",
		icon: "amenities/playschool",
	},
	{
		id: "terrace-private-garden",
		label: "Terrace Private Garden",
		icon: "amenities/terracegarden",
	},
];

interface property_tags_amenities_approvals {
	id: string;
	icon: string;
	label: string;
}

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const AmenitiesCard = ({
	session,
	property_amenities,
	property_created_by,
	property_id,
	dispatch,
	negativeScroll,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	assigned_to,
	amenities_snackbar,
}: {
	session: any;
	property_amenities: Array<property_tags_amenities_approvals>;
	property_created_by: LocalPropertyInterface["created_by"];
	property_id: string | ObjectId;
	dispatch: Function;
	negativeScroll: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	assigned_to: StateInterface["single_property_page"]["property"]["assigned_to"];
	amenities_snackbar: boolean;
}) => {
	const theme = useTheme();

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router: NextRouter = React.useRef(useRouter()).current;

	const localLang = {
		amenities: router.locale === "kn-IN" ? "ಸೌಲಭ್ಯಗಳು" : router.locale === "ar-AE" ? "المرافق" : "Amenities",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		updateSuccess:
			router.locale === "kn-IN"
				? "ಸೌಲಭ್ಯಗಳನ್ನು ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث مرافق العقار بنجاح"
					: "Property amenities updated successfully",
		selectAmenities:
			router.locale === "kn-IN"
				? "ನೀವು ಯಾವುದೇ ಸೌಲಭ್ಯಗಳನ್ನು ಆಯ್ಕೆ ಮಾಡಿಲ್ಲ ಎಂದು ತೋರುತ್ತಿದೆ"
				: router.locale === "ar-AE"
					? "يبدو أنك لم تقم بتحديد أي مرافق"
					: "Looks like you have not selected any amenities",
		startTypingHere:
			router.locale === "kn-IN"
				? "ಇಲ್ಲಿ ಟೈಪ್ ಮಾಡಿ..."
				: router.locale === "ar-AE"
					? "ابدأ الكتابة هنا..."
					: "Start typing here...",
		seeMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية المزيد" : "See more",
		seeLess: router.locale === "kn-IN" ? "ಕಡಿಮೆ ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية أقل" : "See less",
	};

	const [showMore, setShowMore] = React.useState<boolean>(false);

	const [isEditing, setIsEditing] = React.useState<boolean>(false);

	const [errorSnackbar, setErrorSnackbar] = React.useState<boolean>(false);

	const [autoCompleteOpen, setAutoCompleteOpen] = React.useState<boolean | undefined>(false);

	const [load, setLoad] = React.useState<Boolean>(false);

	const profile_context_user: string | ObjectId | null = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	const popperRef = useRef<HTMLDivElement | null>(null);

	React.useEffect(() => {
		if (section_to_edit !== "property-amenities") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	if (loading) {
		return (
			<PaperContainer id="amenities">
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Heading>
				</Contain>

				<ContainerOne>
					<ContentContainer>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							sx={{
								borderRadius: "8px",
							}}
						/>

						<SubHeading variant="body1">
							<Skeleton
								animation="wave"
								variant="text"
								width="6rem"
							/>
						</SubHeading>
					</ContentContainer>

					<ContentContainer>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							sx={{
								borderRadius: "8px",
							}}
						/>

						<SubHeading variant="body1">
							<Skeleton
								animation="wave"
								variant="text"
								width="6rem"
							/>
						</SubHeading>
					</ContentContainer>

					<ContentContainer>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							sx={{
								borderRadius: "8px",
							}}
						/>

						<SubHeading variant="body1">
							<Skeleton
								animation="wave"
								variant="text"
								width="6rem"
							/>
						</SubHeading>
					</ContentContainer>

					<ContentContainer>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							sx={{
								borderRadius: "8px",
							}}
						/>

						<SubHeading variant="body1">
							<Skeleton
								animation="wave"
								variant="text"
								width="6rem"
							/>
						</SubHeading>
					</ContentContainer>

					<ContentContainer>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="2.5rem"
							height="2.5rem"
							sx={{
								borderRadius: "8px",
							}}
						/>

						<SubHeading variant="body1">
							<Skeleton
								animation="wave"
								variant="text"
								width="6rem"
							/>
						</SubHeading>
					</ContentContainer>
				</ContainerOne>
			</PaperContainer>
		);
	}

	const amenitiesContainer: HTMLElement | null = document.getElementById("amenities");

	const handleAboutRemove = (index: number) => {
		const list = [...property_amenities];
		list.splice(index, 1);
		dispatch(setSinglePropertyAmenitiesThunk(list));
	};

	const submit = async () => {
		setLoad(true);
		// if (property_amenities.length === 0) {
		// 	setErrorSnackbar(true);
		// 	return;
		// }
		await dispatch(
			UpdateAmenitiesThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",

				property_id: property_id,
				amenities: property_amenities,
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: property_created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (property_created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (property_created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
			}),
		);
		dispatch(setSectionToEditThunk(""));
		dispatch(setDataBeforeEditThunk({}));
		setIsEditing(false);
		amenitiesContainer
			? amenitiesContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: null;
		setLoad(false);
	};

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_id: ObjectId | string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: property_created_by.user_id != ""
					? (property_created_by.user_id as UserInterface)._id
					: ""
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		profile_context_user !== null
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	const handleErrorSnackbar = (value: boolean) => {
		setErrorSnackbar(value);
	};


	return (
		<PaperContainer
			id="amenities"
			sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
		>
			<Snack
				open={amenities_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.updateSuccess}
				</Alert>
			</Snack>
			<Snack
				open={errorSnackbar}
				autoHideDuration={3000}
				onClose={() => handleErrorSnackbar(false)}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => handleErrorSnackbar(false)}
					severity="error"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.selectAmenities}
				</Alert>
			</Snack>

			<Contain>
				<Heading>{localLang.amenities}</Heading>
				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id="spp_amenities_edit_button"
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("property-amenities"));
									if (section_to_edit !== "property-amenities") {
										switch (section_to_edit) {
											case "property-details":
												dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
												dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
												dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
												dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
												dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
												dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
												dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
												dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
												dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
												dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
												dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
												dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
												dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
												break;
											case "property-config":
												dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
												dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
												dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
												dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
												dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
												dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
												dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
												dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
												dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
												dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
												dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
												dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
												dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
												dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
												dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
												dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
												break;
											case "property-overview":
												dispatch(setSinglePropertyOverviewThunk(data_before_edit));
												break;
											case "about-property":
												dispatch(setSinglePropertyAboutThunk(data_before_edit));
												break;
											case "property-developer":
												dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
												dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
												dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											case "property-vicinity":
												dispatch(setSinglePropertyVicinityThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("property-amenities"));
									}
									setIsEditing(true);
									dispatch(setDataBeforeEditThunk(property_amenities));
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								id="spp_amenities_update_button"
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									setIsEditing(false);
									dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
									setAutoCompleteOpen(false);
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			{!isEditing ? (
				/* User View */
				<React.Fragment>
					<ContainerOne>
						{showMore
							? property_amenities.map((option: property_tags_amenities_approvals, index: number) => {
									return (
										<Tooltip
											key={index}
											title={(option.label.charAt(0).toUpperCase() + option.label.slice(1)).replace(/ /g, " ")}
											placement="top"
										>
											<ContentContainer>
												<ImgTag
													src={
														"/images/icons/singleproperty/amenities/" +
														option.label.toLowerCase().replace(/ /g, "_") +
														".svg"
													}
													referrerPolicy="no-referrer"
												/>

												<SubHeading variant="body1">
													{option.label.length < 15 ? option.label : option.label.substring(0, 15) + "..."}
												</SubHeading>
											</ContentContainer>
										</Tooltip>
									);
								})
							: property_amenities.slice(0, 4).map((option: property_tags_amenities_approvals, index: number) => {
									return (
										<Tooltip
											key={index}
											title={(option.label.charAt(0).toUpperCase() + option.label.slice(1)).replace(/ /g, " ")}
											placement="top"
										>
											<ContentContainer>
												<ImgTag
													src={
														"/images/icons/singleproperty/amenities/" +
														option.label.toLowerCase().replace(/ /g, "_") +
														".svg"
													}
													referrerPolicy="no-referrer"
												/>
												<SubHeading variant="body1">
													{option.label.length < 15 ? option.label : option.label.substring(0, 15) + "..."}
												</SubHeading>
											</ContentContainer>
										</Tooltip>
									);
								})}
						{property_amenities.length > 4 && (
							<ShowMore
								id="spp_amenities_show_more"
								onClick={() => setShowMore(!showMore)}
								sx={{ cursor: "pointer" }}
							>
								{showMore ? localLang.seeLess : localLang.seeMore}
							</ShowMore>
						)}
					</ContainerOne>

					{/* <ContainerMobile>
						{property_amenities.map((option: property_tags_amenities_approvals, index: number) => {
							return (
								<Tooltip
									key={index}
									title={(option.label.charAt(0).toUpperCase() + option.label.slice(1)).replace(/ /g, " ")}
									placement="top"
								>
									<ContentContainer>
										<ImgTag
											src={
												"/images/icons/singleproperty/amenities/" +
												option.label.toLowerCase().replace(/ /g, "_") +
												".svg"
											}
											referrerPolicy="no-referrer"
										/>

										<SubHeading variant="body1">
											{option.label.length < 15 ? option.label : option.label.substring(0, 15) + "..."}
										</SubHeading>
									</ContentContainer>
								</Tooltip>
							);
						})}
					</ContainerMobile> */}
				</React.Fragment>
			) : // /* Edit View */
			// <React.Fragment>
			//   <ContainerEdit>
			//     {/* {property_amenities.map((option: any, index: any) => {
			//       return (
			//         <EditContentContainer key={index}>
			//           <IconButton
			//             color="primary"
			//             sx={{ margin: "0rem 0rem 0.25rem 0rem" }}
			//             aria-label="close"
			//             onClick={() => {
			//               if (section_to_edit !== "property-amenities") {
			//                 dispatch(setSectionToEditThunk("property-amenities"));
			//               }
			//               handleAboutRemove(index);
			//             }}
			//           >
			//             <ClearIcon />
			//           </IconButton>

			//           <ImgContainer>
			//             <ImgContainerOne>
			//               <ImgTag
			//                 src={
			//                   "/images/icons/singleproperty/amenities/" +
			//                   option.label.toLowerCase().replace(/ /g, "_") +
			//                   ".svg"
			//                 }
			//                 referrerPolicy="no-referrer"
			//               />
			//             </ImgContainerOne>
			//           </ImgContainer>
			//           <SubHeading variant="body1">
			//             {option.label.length < 15
			//               ? option.label
			//               : option.label.substring(0, 15) + "..."}
			//           </SubHeading>
			//         </EditContentContainer>
			//       );
			//     })} */}
			//   </ContainerEdit>
			// </React.Fragment>
			null}

			{isEditing ? (
				<Autocomplete
					multiple
					freeSolo
					disablePortal
					open={autoCompleteOpen}
					sx={{
						width: "100%",
						margin: "1.5rem 0rem 1rem 0rem",
						[theme.breakpoints.down("sm")]: {
							margin: "1rem 0rem 1.5rem 0rem",
						},
					}}
					id="spp_amenities_edit_autocomplete"
					value={property_amenities}
					onChange={(event, newValue: amenity[]) => {
						if (section_to_edit !== "property-amenities") {
							dispatch(setSectionToEditThunk("property-amenities"));
						}

						dispatch(setSinglePropertyAmenitiesThunk(newValue));
						setAutoCompleteOpen(false);
					}}
					options={AmenityOptions.sort((a: any, b: any) => {
						return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
					})}
					getOptionLabel={(option: amenity) => option.label}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							label={localLang.startTypingHere}
							onBlur={() => {
								setAutoCompleteOpen(false);
							}}
							onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
								if (e.target.value !== "" && e.target.value.length > 2) {
									setAutoCompleteOpen(true);
								}
							}}
						/>
					)}
					renderOption={(props, option, { selected }, index) => {
						const { key, ...rest } = props;
						
						return (
							<li
								key={key}
								{...rest}
							>
								<Checkbox
									id="spp_amenities_edit_checkbox"
									icon={<CheckBoxOutlineBlankIcon />}
									checkedIcon={<CheckBoxIcon />}
									checked={selected}
								/>
								{(option as amenity).label}
							</li>
						);
					}}
				/>
			) : null}

			{isEditing ? (
				<>
					{load ? (
						<CircularProgress
							// size={24}
							sx={{
								marginTop: "1rem",
							}}
						/>
					) : (
						<BeegruButton
							id="spp_amenities_update_button"
							flavor="primary"
							onClick={submit}
							variant="contained"
							sx={{ width: "100%" }}
							// sx={{
							//   [theme.breakpoints.up("sm")]: {
							//     margin: "1rem 0rem 0rem 0rem",
							//   },
							// }}
						>
							{localLang.update}
						</BeegruButton>
					)}
				</>
			) : null}
		</PaperContainer>
	);
};

export default AmenitiesCard;
